import { useAuthStore } from "@/stores/authStore";
import { useFilterStore } from "@/stores/filterStore";

// dev 서버에선 항상 상대경로("")를 써서 Vite 프록시(/analytics)를 태운다(2026-07-24) —
// 프로덕션 CX 엔드포인트 CORS 가 https://novadb.sedaily.ai 만 허용해 localhost 직접 호출이
// 막히므로, 브라우저→localhost(동일 출처)→Vite→프로드(서버-사이드) 로 CORS 를 우회한다.
// 프로덕션 빌드(import.meta.env.DEV=false)는 설정된 절대 URL 을 그대로 사용 — 배포 무영향.
const BASE_URL = import.meta.env.DEV ? "" : (import.meta.env.VITE_ANALYTICS_API_BASE_URL || "");

// STG QA 크레딧 지급 전용 API(B-2) — analytics 와 다른 API Gateway(전용 Lambda·전용 role).
// dev 에선 상대경로("")로 두면 Vite 프록시가 필요하지만, 이 기능은 STG 뷰 전용이라 dev
// 프리뷰에서 호출되지 않는다(버튼이 env==="stg" 에서만 뜨고, 로컬은 기본 prod). 프로덕션
// 빌드는 설정된 절대 URL 을 그대로 쓴다. 미설정 시 빈 문자열 → 호출 시 명확히 실패한다.
const STG_QA_API_BASE_URL = import.meta.env.VITE_STG_QA_API_BASE_URL || "";

class ApiError extends Error {
  constructor(message, status, code) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

function buildQuery(params) {
  const usp = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") usp.set(k, String(v));
  });
  const s = usp.toString();
  return s ? `?${s}` : "";
}

// GET 응답 인메모리 캐시 — 탭 전환·날짜피커 재클릭 등으로 같은 조회가 짧은 시간 안에
// 반복될 때 네트워크·Lambda 재호출을 건너뛴다(비용 없는 프론트 전용 최적화).
// TTL은 백엔드 인메모리 캐시(45초)보다 짧게 잡아 "프론트가 백엔드보다 오래 낡은 값을
// 들고 있는" 상황을 피한다. NC 조정 등 변경 요청 성공 시 전체 무효화(아래) — 재조회가
// 방금 바뀐 값을 캐시에서 잘못 재사용하지 않도록.
const _GET_CACHE_TTL_MS = 20_000;
const _getCache = new Map(); // url -> { ts, data }

async function request(path, { params, signal, method = "GET", body, baseUrl } = {}) {
  const idToken = await useAuthStore.getState().getValidIdToken();

  // STG VIEW = READ ONLY (2026-09-10, 결함 B).
  // 모든 mutation(비-GET) 요청에 현재 조회 env 를 query param 으로 강제로 싣는다.
  // 이렇게 해야 STG 화면에서 새어나간 write 가 백엔드 STG_READ_ONLY(403) 최종 방어선에
  // 반드시 걸린다(프론트 UI disable 은 보조 방어). env 는 filterStore 의 화이트리스트
  // (setEnv 가 prod|stg 로만 정규화)를 이미 통과한 값이라 raw 문자열이 아니다.
  // 읽기(GET)는 useAnalyticsQuery 가 params.env 를 이미 주입하므로 여기서 건드리지 않는다
  // (직접 호출하는 read 는 기존과 동일하게 env 미포함 = 레거시 동작 유지).
  let effectiveParams = params;
  if (method !== "GET" && baseUrl === undefined) {
    const viewEnv = useFilterStore.getState().env;   // "prod" | "stg"
    effectiveParams = { ...(params || {}), env: viewEnv };
  }

  const url = `${baseUrl !== undefined ? baseUrl : BASE_URL}${path}${buildQuery(effectiveParams)}`;

  if (method === "GET") {
    const cached = _getCache.get(url);
    if (cached && Date.now() - cached.ts < _GET_CACHE_TTL_MS) {
      return cached.data;
    }
  }

  const res = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    signal,
  });

  let responseBody = null;
  try { responseBody = await res.json(); } catch { /* non-JSON response */ }

  if (!res.ok) {
    const code = responseBody?.error || `HTTP_${res.status}`;
    const msg = responseBody?.message || res.statusText;
    if (res.status === 401) {
      // 401 = 세션 만료/무효 — 로그인 화면으로 돌려보낸다.
      // 403 은 여기서 처리하지 않는다: 403 은 "권한/정책상 이 작업 거부"(예: staging READ_ONLY 로
      // write 차단)이지 세션 만료가 아니다. 403 을 signOut 하면 정상 로그인 사용자가 write 시도
      // 시 로그아웃되는 오작동이 난다(CX '나에게 할당' 클릭 → 로그아웃 사고). 403 은 아래에서
      // ApiError 로 던져 호출부(UI)가 메시지를 그대로 표시하게 한다.
      if (useAuthStore.getState().idToken) {
        useAuthStore.getState().signOut();
      }
    }
    throw new ApiError(msg, res.status, code);
  }

  if (method === "GET") {
    _getCache.set(url, { ts: Date.now(), data: responseBody });
  } else {
    // 변경 요청(NC 조정 등) 성공 — 캐시된 GET 결과가 방금 바뀐 값과 어긋날 수 있어 전체 무효화.
    _getCache.clear();
  }
  return responseBody;
}

export const analyticsApi = {
  overview:        (params, signal) => request("/analytics/overview", { params, signal }),
  briefingAi:      (params, signal) => request("/analytics/briefing-ai", { params, signal }),
  byTool:          (params, signal) => request("/analytics/by-tool", { params, signal }),
  byModel:         (params, signal) => request("/analytics/by-model", { params, signal }),
  mystyle:         (params, signal) => request("/analytics/mystyle", { params, signal }),
  timeseries:      (params, signal) => request("/analytics/timeseries", { params, signal }),
  finops:          (params, signal) => request("/analytics/finops", { params, signal }),
  devops:          (params, signal) => request("/analytics/devops", { params, signal }),
  internalUsers:   (params, signal) => request("/analytics/internal-users", { params, signal }),
  legacyInternal:  (params, signal) => request("/analytics/legacy-internal", { params, signal }),
  topUsers:        (params, signal) => request("/analytics/users/top", { params, signal }),
  userDetail:      (userId, params, signal) =>
    request(`/analytics/users/${encodeURIComponent(userId)}`, { params, signal }),
  toolDistribution: (tool, params, signal) =>
    request(`/analytics/tools/${encodeURIComponent(tool)}/distribution`, { params, signal }),

  // Canonical Identity Phase 1 (2026-09-10, read-only) — 동일 사용자의 여러 sub 를
  // 하나로 묶어 연결된 identity + PROD/STG 환경별 wallet 을 조회한다. GET 이라 env 주입
  // 대상이 아니다(읽기). 매핑 없으면 백엔드가 404(CANONICAL_NOT_FOUND) — 호출부는 기존
  // sub 조회(userDetail)로 fallback.
  canonicalUser: (canonicalUserId, params, signal) =>
    request(`/analytics/canonical-users/${encodeURIComponent(canonicalUserId)}`, { params, signal }),

  // 관리자 전용 — NC 수동 지급/회수 (Cognito 로그인 필요, novadb 백엔드가 email 허용목록 확인)
  // adminUserDetail: GET 이라 env 자동주입 대상이 아니므로 호출부가 view env 를 명시 전달한다
  // (B-3, 2026-09-30) — STG 뷰에서 CX '현재 잔액'이 PROD 를 보던 버그 수정. env 미지정=레거시(prod).
  adminUserDetail:  (userId, { env } = {}, signal) =>
    request(`/analytics/admin/users/${encodeURIComponent(userId)}`, { params: { env }, signal }),
  adminAdjustCredit: (userId, { amount, reason }, signal) =>
    request(`/analytics/admin/users/${encodeURIComponent(userId)}/adjust`, {
      method: "POST", body: { amount, reason }, signal,
    }),

  // 관리자 전용 — 일괄 NC 지급/회수 (가입일 범위 또는 이메일 목록 대상)
  adminBulkPreview: (targets, signal) =>
    request("/analytics/admin/bulk-adjust/preview", { method: "POST", body: { targets }, signal }),
  adminBulkAdjust: ({ targets, amount, reason }, signal) =>
    request("/analytics/admin/bulk-adjust", { method: "POST", body: { targets, amount, reason }, signal }),
  adminMailSend: ({ emails, subject, bodyText, bodyHtml, testMode }, signal ) =>
    request("/analytics/admin/mail/send", { method: "POST", body: { emails, subject, bodyText, bodyHtml, testMode }, signal }),

  // 관리자 전용 — 계정 역할 부여/해제 (admin|test|none, 2026-08-05)
  adminSetAccountRole: ({ email, role }, signal) =>
    request("/analytics/admin/account-role", { method: "POST", body: { email, role }, signal }),

  // STG 전용 — QA 테스트 크레딧 단건 지급 (B-2). analytics 와 다른 전용 API Gateway/Lambda/role.
  // STG 뷰에서만 호출된다(버튼이 env==="stg" 에서만 노출). 인증은 기존과 동일하게 request()가
  // 붙이는 Cognito ID 토큰(Authorization: Bearer). baseUrl 을 QA API 로 지정해 analytics 로
  // 안 간다. PROD 원장과 무관 — 전용 role 이 nova-*-stg 만 write 가능.
  stgQaGrant: ({ sub, email, amount, reason }, signal) =>
    request("/stg-qa/grant", {
      method: "POST",
      baseUrl: STG_QA_API_BASE_URL,
      body: { sub, email, amount, reason },
      signal,
    }),

  // 관리자 전용 — 운영비(지출) 수동 입력 (2026-07-17, AWS 인프라비 등 자동 추적이 안
  // 되는 비용을 "매출·마진 추이"의 진짜 순이익 계산에 반영)
  // OpenAI 조직 Costs API 일별 실지출(자동) — 수기 입력과 별개(2026-07-19).
  openaiCosts: {
    list: (params, signal) => request("/analytics/openai-costs", { params, signal }),
  },
  // BillingON 일별 AWS 실청구액(USD) — 수기 엑셀 입력 대체(2026-07-23). granularity=D|M.
  billingonCosts: {
    list: (params, signal) => request("/analytics/billingon-costs", { params, signal }),
  },
  // 고객 문의 — 자체 상담 티켓 + VOC (2026-07-19, 채널톡 대체)
  cx: {
    tickets: (params, signal) => request("/analytics/cx/tickets", { params, signal }),
    ticketUpdate: (id, body, signal) =>
      request(`/analytics/cx/tickets/${encodeURIComponent(id)}`, { method: "POST", body, signal }),
    voc: (params, signal) => request("/analytics/cx/voc", { params, signal }),
  },
  expenses: {
    list:   (params, signal) => request("/analytics/expenses", { params, signal }),
    create: (body, signal) => request("/analytics/expenses", { method: "POST", body, signal }),
    // 백엔드가 GetItem 권한 없이 GSI(dateBucket) Query 로 행을 찾아 소프트 삭제 — date 필수.
    remove: (id, date, signal) => request(`/analytics/expenses/${encodeURIComponent(id)}`, { method: "DELETE", params: { date }, signal }),
    // 일할 안분 묶음 전체 삭제 — id 자리에 groupId, 기간을 쿼리로(백엔드가 그 기간을 스캔해 void).
    removeGroup: (groupId, { periodFrom, periodTo }, signal) =>
      request(`/analytics/expenses/${encodeURIComponent(groupId)}`, {
        method: "DELETE", params: { group: 1, periodFrom, periodTo }, signal,
      }),
  },
};

export { ApiError };
